
import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

// Components
import DashboardSidebarAdmin from '../components/Dashboard/DashboardSidebarAdmin';
import DashboardNavbarAdmin from '../components/Dashboard/DashboardNavbarAdmin';

const AdminProvidersPage = () => {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [currentProvider, setCurrentProvider] = useState(null);

    // Form inputs
    const [formData, setFormData] = useState({
        name: '',
        balance_usd: '',
        credit_balance: '',
    });

    useEffect(() => {
        fetchProviders();
    }, []);

    const fetchProviders = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('providers')
                .select('*')
                .order('created_at', { ascending: true });

            if (error) {
                // Determine if error is likely due to missing table
                if (error.code === '42P01') {
                    console.warn("Table 'providers' does not exist yet.");
                    setProviders([]);
                } else {
                    throw error;
                }
            } else {
                setProviders(data || []);
            }
        } catch (err) {
            console.error('Error fetching providers:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (mode, provider = null) => {
        setModalMode(mode);
        setCurrentProvider(provider);
        if (mode === 'edit' && provider) {
            setFormData({
                name: provider.name,
                balance_usd: provider.balance_usd,
                credit_balance: provider.credit_balance,
            });
        } else {
            setFormData({
                name: '',
                balance_usd: '',
                credit_balance: '',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentProvider(null);
        setFormData({ name: '', balance_usd: '', credit_balance: '' });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: formData.name,
                balance_usd: parseFloat(formData.balance_usd) || 0,
                credit_balance: parseFloat(formData.credit_balance) || 0,
            };

            if (modalMode === 'add') {
                const { error } = await supabase
                    .from('providers')
                    .insert([payload]);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('providers')
                    .update(payload)
                    .eq('id', currentProvider.id);
                if (error) throw error;
            }

            // Refresh data
            fetchProviders();
            handleCloseModal();
        } catch (err) {
            console.error('Error saving provider:', err);
            alert('Gagal menyimpan data provider: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Apakah Anda yakin ingin menghapus provider ini?')) return;
        try {
            const { error } = await supabase
                .from('providers')
                .delete()
                .eq('id', id);
            if (error) throw error;
            fetchProviders();
        } catch (err) {
            console.error('Error deleting provider:', err);
            alert('Gagal menghapus provider: ' + err.message);
        }
    };

    return (
        <div className="h-screen overflow-hidden bg-slate-50 font-sans text-slate-800">
            <div className="flex h-full min-w-0">
                <DashboardSidebarAdmin />

                <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
                    <DashboardNavbarAdmin displayName="Admin" />

                    <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
                        <div className="max-w-6xl mx-auto space-y-6">

                            {/* Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-slate-900">Provider & Saldo</h1>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Kelola daftar provider dan monitoring saldo deposit/kredit.
                                    </p>
                                </div>
                                <button
                                    onClick={() => handleOpenModal('add')}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg shadow transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Tambah Provider
                                </button>
                            </div>

                            {/* Content */}
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                {loading ? (
                                    <div className="p-12 text-center">
                                        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
                                        <p className="text-slate-500">Memuat data provider...</p>
                                    </div>
                                ) : providers.length === 0 ? (
                                    <div className="p-12 text-center">
                                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-medium text-slate-900">Belum ada provider</h3>
                                        <p className="text-slate-500 mt-1 mb-6">Tambahkan provider pertama Anda untuk mulai mengelola saldo.</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                                <tr>
                                                    <th className="px-6 py-4">Nama Provider</th>
                                                    <th className="px-6 py-4">Saldo Dollar (USD)</th>
                                                    <th className="px-6 py-4">Saldo Kredit</th>
                                                    <th className="px-6 py-4 text-center">Aksi</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {providers.map((p) => (
                                                    <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                                                        <td className="px-6 py-4 font-medium text-slate-900">
                                                            {p.name}
                                                        </td>
                                                        <td className="px-6 py-4 text-emerald-700 font-mono font-medium">
                                                            $ {p.balance_usd?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                                        </td>
                                                        <td className="px-6 py-4 text-blue-700 font-mono font-medium">
                                                            {p.credit_balance?.toLocaleString('id-ID') || '0'}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <button
                                                                    onClick={() => handleOpenModal('edit', p)}
                                                                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                    title="Edit"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(p.id)}
                                                                    className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                                    title="Hapus"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                        </div>
                    </main>
                </div>
            </div>

            {/* Modal Form */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold text-slate-900">
                                {modalMode === 'add' ? 'Tambah Provider Baru' : 'Edit Provider'}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Nama Provider</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    placeholder="Contoh: OpenAI, KlingAI"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Saldo Dollar (USD)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.balance_usd}
                                            onChange={(e) => setFormData({ ...formData, balance_usd: e.target.value })}
                                            className="w-full pl-7 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Saldo Kredit</label>
                                    <input
                                        type="number"
                                        value={formData.credit_balance}
                                        onChange={(e) => setFormData({ ...formData, credit_balance: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm shadow-blue-200 transition-colors"
                                >
                                    Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminProvidersPage;
